import {
  CheckboxFilterSchema,
  DateFilterSchema,
  Filter,
  FilterItem,
  FilterTabs,
  FilterType,
  NumericFilterSchema,
  SalesDataFilterSchema,
  SearchFilterComparisonOperator,
  SearchQuery,
  SearchQuerySchema,
  SearchType,
  ValidAgeRangeFilterSchema,
} from "./types";
import { parseUrlDate } from "./constants";
import {
  getActiveSalesDataMetrics,
  parseSalesDataMetricFilterOperator,
  salesDataMetricDefinitions,
  type SalesDataMetricKey,
} from "./sales-data-filter-utils";

export { parseComparisonOperator } from "./comparison-operator-utils";

export {
  buildElasticsearchRequest as searchQueryToProductSearchRequest,
  formatSearchScore,
} from "./adapters";

function tokenizeFilter(
  filter: string,
): { field: string; op: string; value: string } | undefined {
  const i1 = filter.indexOf(".");
  if (i1 <= 0) return undefined;
  const i2 = filter.indexOf(".", i1 + 1);
  if (i2 < 0 || i2 === i1 + 1 || i2 === filter.length - 1) return undefined;
  return {
    field: filter.slice(0, i1),
    op: filter.slice(i1 + 1, i2),
    value: filter.slice(i2 + 1),
  };
}

export function parseParamFilters(
  filters: string[],
  defaultFilters: FilterItem[],
): SearchQuery["filters"] {
  if (!filters.length) return {};

  const result: Record<string, Filter> = {};
  const dateBounds = new Map<string, { gte?: string; lte?: string }>();
  const ageBounds = new Map<
    string,
    { ageType?: string; gte?: string; lte?: string }
  >();
  const checkboxValues = new Map<string, string[]>();
  const salesDataValues = new Map<
    string,
    {
      gte?: string;
      lte?: string;
      metrics?: Partial<
        Record<
          SalesDataMetricKey,
          {
            comparisonOperator?: SearchFilterComparisonOperator;
            value?: number;
          }
        >
      >;
    }
  >();

  filters.forEach((filter) => {
    const token = tokenizeFilter(filter);
    if (!token) return;

    const filterItem = defaultFilters.find(
      (f) => f.name === token.field && f.show === true,
    );
    if (!filterItem) return;

    const { field, op, value } = token;

    switch (filterItem.type) {
      case FilterType.NUMERIC: {
        const parsed = NumericFilterSchema.safeParse({
          value: Number(value),
          comparisonOperator: op,
          type: FilterType.NUMERIC,
        });
        if (parsed.success) result[field] = parsed.data;
        return;
      }
      case FilterType.DATE_RANGE: {
        const bounds = dateBounds.get(field) ?? {};
        if (op === "eq") {
          bounds.gte = value;
          bounds.lte = value;
        } else if (op === "gte" || op === "lte") {
          bounds[op] = value;
        } else {
          return;
        }
        dateBounds.set(field, bounds);
        return;
      }
      case FilterType.AGE_RANGE: {
        const bounds = ageBounds.get(field) ?? {};
        if (op === "eq") {
          bounds.ageType = value;
        } else if (op === "gte" || op === "lte") {
          bounds[op] = value;
        } else {
          return;
        }
        ageBounds.set(field, bounds);
        return;
      }
      case FilterType.CHECKBOX: {
        if (op !== "in") return;
        const acc = checkboxValues.get(field) ?? [];
        acc.push(value);
        checkboxValues.set(field, acc);
        break;
      }
      case FilterType.SALES_DATA: {
        const salesData = salesDataValues.get(field) ?? {};
        if (op === "eq") {
          salesData.gte = value;
          salesData.lte = value;
          salesDataValues.set(field, salesData);
          break;
        }
        if (op === "gte" || op === "lte") {
          salesData[op] = value;
          salesDataValues.set(field, salesData);
          break;
        }

        const salesDataMetricOperator = parseSalesDataMetricFilterOperator(op);
        if (!salesDataMetricOperator) break;

        const parsed = NumericFilterSchema.safeParse({
          value: Number(value),
          comparisonOperator: salesDataMetricOperator.comparisonOperator,
          type: FilterType.NUMERIC,
        });
        if (!parsed.success || !parsed.data.comparisonOperator) break;

        salesData.metrics = {
          ...salesData.metrics,
          [salesDataMetricOperator.metricKey]: {
            comparisonOperator: parsed.data.comparisonOperator,
            value: parsed.data.value,
          },
        };
        salesDataValues.set(field, salesData);
        break;
      }
      default:
    }
  });

  dateBounds.forEach(({ gte, lte }, field) => {
    if (!gte || !lte) return;
    let from: string;
    let to: string;
    try {
      from = parseUrlDate(gte);
      to = parseUrlDate(lte);
    } catch {
      return;
    }
    const parsed = DateFilterSchema.safeParse({
      tab: from === to ? FilterTabs.SPECIFIC : FilterTabs.RANGE,
      range: { from, to },
      type: FilterType.DATE_RANGE,
    });
    if (parsed.success) result[field] = parsed.data;
  });

  ageBounds.forEach(({ ageType, gte, lte }, field) => {
    if (!ageType || gte === undefined || lte === undefined) return;
    const min = Number(gte);
    const max = Number(lte);
    if (Number.isNaN(min) || Number.isNaN(max)) return;
    const parsed = ValidAgeRangeFilterSchema.safeParse({
      ageType,
      range: { min, max },
      type: FilterType.AGE_RANGE,
    });
    if (parsed.success) result[field] = parsed.data;
  });

  checkboxValues.forEach((values, field) => {
    if (!values.length) return;
    const parsed = CheckboxFilterSchema.safeParse({
      values,
      type: FilterType.CHECKBOX,
    });
    if (parsed.success) result[field] = parsed.data;
  });

  salesDataValues.forEach(({ gte, lte, metrics }, field) => {
    let range = { from: "", to: "" };
    if (gte && lte) {
      try {
        range = { from: parseUrlDate(gte), to: parseUrlDate(lte) };
      } catch {
        range = { from: "", to: "" };
      }
    }

    const parsed = SalesDataFilterSchema.safeParse({
      type: FilterType.SALES_DATA,
      range,
      ...Object.fromEntries(
        salesDataMetricDefinitions.map(({ key }) => [
          key,
          {
            comparisonOperator:
              metrics?.[key]?.comparisonOperator ??
              SearchFilterComparisonOperator.GREATER,
            value: metrics?.[key]?.value,
          },
        ]),
      ),
    });
    if (parsed.success && getActiveSalesDataMetrics(parsed.data).length > 0) {
      result[field] = parsed.data;
    }
  });

  return result as SearchQuery["filters"];
}

export function paramsToSearchQuery(
  params: URLSearchParams,
  defaultFilters: FilterItem[],
): SearchQuery {
  const filters = parseParamFilters(params.getAll("filter"), defaultFilters);
  const query = params.get("q");
  const typeParam = params.get("type");
  const typeParseResult = SearchQuerySchema.pick({ type: true }).safeParse({
    type: typeParam ?? undefined,
  });
  const resolvedType = typeParseResult.success
    ? typeParseResult.data.type
    : SearchType.QUICK;
  const isSemantic = resolvedType === SearchType.SEMANTIC;
  const page = isSemantic ? null : params.get("page");
  const pageSize = isSemantic ? null : params.get("pageSize");

  return SearchQuerySchema.parse({
    type: resolvedType,
    query: query ?? "",
    filters,
    page,
    pageSize,
  });
}

export function isTopMatchesSearch(
  type: SearchType | undefined,
  query: string | undefined,
): boolean {
  return (
    (type === SearchType.SEMANTIC || type === SearchType.HYBRID) &&
    (query?.trim() ?? "").length > 0
  );
}
